'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface VerticalItem {
  name: string;
  riskLevel: 'high' | 'medium' | 'low';
  requirements: string[];
  platforms: string[];
  restrictions: string[];
}

interface VerticalComplianceProps {
  verticals: VerticalItem[];
}

export default function VerticalCompliance({ verticals }: VerticalComplianceProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'high':
        return 'text-error bg-error/10';
      case 'medium':
        return 'text-warning bg-warning/10';
      case 'low':
        return 'text-success bg-success/10';
      default:
        return 'text-text-secondary bg-muted';
    }
  };

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <div className="space-y-4">
      {verticals.map((vertical, index) => (
        <div key={index} className="bg-card border border-border rounded-sm overflow-hidden">
          <button
            onClick={() => toggleExpand(index)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors duration-smooth"
          >
            <div className="flex items-center space-x-4">
              <span
                className={`px-3 py-1 rounded-sm font-accent text-xs font-medium uppercase ${getRiskColor(
                  vertical.riskLevel
                )}`}
              >
                {vertical.riskLevel} Risk
              </span>
              <h3 className="font-headline font-semibold text-base text-primary">
                {vertical.name}
              </h3>
            </div>
            <Icon
              name={expandedIndex === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
              size={20}
              variant="outline"
              className="text-text-secondary"
            />
          </button>

          {expandedIndex === index && (
            <div className="px-6 py-4 border-t border-border bg-surface">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h4 className="font-headline font-semibold text-sm text-primary mb-3 flex items-center space-x-2">
                    <Icon name="DocumentCheckIcon" size={16} variant="outline" />
                    <span>Compliance Requirements</span>
                  </h4>
                  <ul className="space-y-2">
                    {vertical.requirements.map((req, reqIndex) => (
                      <li key={reqIndex} className="font-body text-xs text-text-secondary">
                        • {req}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-headline font-semibold text-sm text-primary mb-3 flex items-center space-x-2">
                    <Icon name="GlobeAltIcon" size={16} variant="outline" />
                    <span>Platform Access</span>
                  </h4>
                  <ul className="space-y-2">
                    {vertical.platforms.map((platform, platformIndex) => (
                      <li
                        key={platformIndex}
                        className="font-body text-xs text-text-primary flex items-center space-x-2"
                      >
                        <Icon
                          name="CheckIcon"
                          size={14}
                          variant="outline"
                          className="text-success"
                        />
                        <span>{platform}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-headline font-semibold text-sm text-primary mb-3 flex items-center space-x-2">
                    <Icon name="ExclamationCircleIcon" size={16} variant="outline" />
                    <span>Restrictions</span>
                  </h4>
                  <ul className="space-y-2">
                    {vertical.restrictions.map((restriction, restrictionIndex) => (
                      <li key={restrictionIndex} className="font-body text-xs text-text-secondary">
                        • {restriction}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}